import {isEqual, pickBy} from 'lodash';

import {normalizeCountry} from '../ManageMySite.constants';
import {
  BusinessProfileApiPayload,
  BusinessProfileApiResponse,
  EditFormPayload,
  ViewData,
} from '../ManageMySite.types';
import {splitDisplayName, stripPhoneToDigits} from '../util/manageMySite.util';

/**
 * Maps API response to view data format
 */
export const mapProfileResponseToViewData = (
  profile: BusinessProfileApiResponse,
): ViewData => {
  const firstName = profile.firstName || '';
  const lastName = profile.lastName || '';

  return {
    avatarUrl: profile.avatarUrl || '',
    webAlias: profile.webAlias || '',
    baseWebsiteUrl: profile.baseWebsiteUrl || '',
    firstName,
    lastName,
    displayName: `${firstName} ${lastName}`.trim(),
    email: profile.email || '',
    phonePrimary: profile.phone?.primary || '',
    phoneSecondary: profile.phone?.secondary || '',
    country: profile.displayAddress?.country || 'US',
    street:
      [profile.displayAddress?.address1, profile.displayAddress?.address2]
        .filter(Boolean)
        .join(', ') || '',
    city: profile.displayAddress?.city || '',
    state: profile.displayAddress?.state || '',
    zipcode: profile.displayAddress?.zip || '',
    about: profile.aboutMe || '',
    transformationBeforeUrl: profile.beforeImageUrl || '',
    transformationAfterUrl: profile.afterImageUrl || '',
  };
};

/**
 * Builds API payload from form data
 */
export const buildBusinessProfilePayload = (
  payload: EditFormPayload,
): BusinessProfileApiPayload => {
  const {firstName, lastName} = splitDisplayName(payload.displayName);

  return {
    webAlias: payload.webAlias.trim().toLowerCase(),
    firstName,
    lastName,
    email: payload.email.trim(),
    phone: {
      primary: stripPhoneToDigits(payload.phonePrimary),
      secondary: stripPhoneToDigits(payload.phoneSecondary),
    },
    displayAddress: {
      address1: payload.street.trim(),
      city: payload.city.trim(),
      state: payload.state,
      zip: payload.zipcode.trim(),
      country: normalizeCountry(payload.country),
    },
    aboutMe: payload.about,
  };
};

/**
 * Builds PATCH payload with only changed fields
 * Always includes firstName, lastName, and webAlias
 */
export const buildPatchPayload = (
  originalData: ViewData,
  newPayload: EditFormPayload,
): Partial<BusinessProfileApiPayload> => {
  const newApiPayload = buildBusinessProfilePayload(newPayload);
  const oldApiPayload = buildBusinessProfilePayload(
    originalData as EditFormPayload,
  );

  // Compare and keep only changed fields
  const changedFields = pickBy(newApiPayload, (value, key) => {
    return !isEqual(
      value,
      oldApiPayload[key as keyof BusinessProfileApiPayload],
    );
  });

  // Always include these three fields + any changed fields
  const changes = {
    firstName: newApiPayload.firstName,
    lastName: newApiPayload.lastName,
    webAlias: newApiPayload.webAlias,
    ...changedFields,
  };

  return changes;
};

export const mapEditPayloadToViewData = (
  prev: ViewData,
  payload: EditFormPayload,
): ViewData => ({
  ...prev,
  webAlias: payload.webAlias,
  displayName: payload.displayName,
  email: payload.email,
  phonePrimary: payload.phonePrimary,
  phoneSecondary: payload.phoneSecondary,
  country: payload.country,
  street: payload.street,
  city: payload.city,
  state: payload.state,
  zipcode: payload.zipcode,
  about: payload.about,
});
